import type {ReactNode} from 'react';
import {
  Box,
  Flex,
  Group,
  Paper,
  Text,
  ThemeIcon,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import {IconExternalLink, IconFilter} from '@tabler/icons-react';

/**
 * Clickable stat/count card with an active state and optional filter/navigate
 * affordance — used for dashboard summary tiles.
 * @registry Clickable metric card (title/count/icon) with filter or navigate mode.
 *
 * Ported from `library-m/data-display/StatCard`. Prop surface kept identical.
 */
export interface StatCardProps {
  title: string;
  count: number;
  icon: ReactNode;
  color: string;
  isActive: boolean;
  onClick: () => void;
  tooltip?: string;
  mode?: 'filter' | 'navigate' | 'none';
}

export const StatCard = ({
  title,
  count,
  icon,
  color,
  isActive,
  onClick,
  tooltip,
  mode = 'filter',
}: StatCardProps) => {
  const defaultTooltip =
    mode === 'navigate' ? `Go to ${title}` : 'Click to filter';

  return (
    <Tooltip
      label={tooltip ?? defaultTooltip}
      position="top"
      withArrow
      openDelay={400}>
      <UnstyledButton onClick={onClick} w="100%" h="100%">
        <Paper
          p="md"
          withBorder
          shadow="xs"
          h="100%"
          style={{
            borderColor: isActive
              ? `var(--mantine-color-${color}-6)`
              : 'var(--mantine-color-gray-3)',
            borderWidth: 2,
            transition: 'all 0.2s ease',
            background: isActive
              ? `var(--mantine-color-${color}-0)`
              : 'var(--mantine-color-body)',
          }}>
          <Flex direction="column" h="100%" justify="space-between">
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <Box style={{minWidth: 0}}>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600} lineClamp={2}>
                  {title}
                </Text>
                <Text size="xl" fw={700} mt={4}>
                  {count}
                </Text>
              </Box>
              <ThemeIcon
                size="lg"
                variant={isActive ? 'filled' : 'light'}
                color={color}
                style={{flexShrink: 0}}>
                {icon}
              </ThemeIcon>
            </Group>
            {mode !== 'none' && (
              <Group gap={4} mt="xs">
                {mode === 'navigate' ? (
                  <>
                    <IconExternalLink
                      size={12}
                      color={`var(--mantine-color-${color}-6)`}
                    />
                    <Text size="xs" c={color} fw={500}>
                      Open
                    </Text>
                  </>
                ) : (
                  <>
                    <IconFilter
                      size={12}
                      color={
                        isActive
                          ? `var(--mantine-color-${color}-6)`
                          : 'var(--mantine-color-dimmed)'
                      }
                    />
                    <Text size="xs" c={isActive ? color : 'dimmed'} fw={500}>
                      {isActive ? 'Filtered' : 'Filter'}
                    </Text>
                  </>
                )}
              </Group>
            )}
          </Flex>
        </Paper>
      </UnstyledButton>
    </Tooltip>
  );
};
